import {useState} from "react";
import {Mail, Phone, Clock, CheckCircle2, AlertCircle} from "lucide-react";
import useAPIService from "../../../hooks/useAPIService";

// DHW brand palette (see project requirements doc).
const DHW_BLUE = "#001489";

// Mirrors the public website's contact form (idaho-et-site/contact.html):
// same fields and the same Case Management Services Line. Submissions POST to
// /contact-message, which emails the program inbox server-side (the old
// mailto: handoff silently failed for anyone without a configured mail app).
const SUPPORT_PHONE_DISPLAY = "1-866-777-3850";
const SUPPORT_PHONE_TEL = "+18667773850";

const Field = ({
  id,
  label,
  type = "text",
  required,
  value,
  onChange,
  ...rest
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-semibold text-[#191919]">
      {label}{" "}
      {required ? (
        <span className="text-rose-600">*</span>
      ) : (
        <span className="font-normal text-slate-400">(optional)</span>
      )}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-[#191919] focus:border-[#001489] focus:outline-none focus:ring-2 focus:ring-[#001489]/20"
      {...rest}
    />
  </div>
);

const ContactPage = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  // idle -> sending -> sent | error (with a plain-language message)
  const [status, setStatus] = useState({state: "idle"});
  const {apiFetch} = useAPIService();

  const update = (e) => {
    setForm((f) => ({...f, [e.target.name]: e.target.value}));
    // A stale "sent"/error notice next to edited fields reads as if it
    // applies to the new text; reset until the next submit.
    if (status.state === "sent" || status.state === "error") {
      setStatus({state: "idle"});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "sending") return;
    setStatus({state: "sending"});
    try {
      await apiFetch("/contact-message", {
        method: "POST",
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
        }),
      });
      setStatus({state: "sent"});
      setForm({firstName: "", lastName: "", email: "", phone: "", message: ""});
    } catch (err) {
      setStatus({
        state: "error",
        // err.status means the server answered (validation / rate limit) with
        // a participant-readable message; anything else is a network failure.
        message: err.status
          ? err.message
          : "We could not send your message. Please check your internet " +
            "connection and try again.",
      });
    }
  };

  return (
    <div className="min-h-full bg-white pb-10 font-sans">
      <div className="mb-6">
        <p
          className="text-xs font-bold uppercase tracking-wider"
          style={{color: DHW_BLUE}}
        >
          Contact us
        </p>
        <h2 className="text-2xl font-bold text-[#191919]">
          We are here to help
        </h2>
        <p className="mt-1 max-w-2xl text-base text-slate-600">
          Call us or send a message below. If you are new and need help getting
          started, or you are not sure what to do next, our team can guide you.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr,1fr]">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="rounded-[2rem] border border-[#ECECEC] bg-white p-5 shadow-sm md:p-7"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="firstName"
              label="First name"
              required
              value={form.firstName}
              onChange={update}
            />
            <Field
              id="lastName"
              label="Last name"
              required
              value={form.lastName}
              onChange={update}
            />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field
              id="email"
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={update}
            />
            <Field
              id="phone"
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={update}
            />
          </div>
          <div className="mt-4 flex flex-col gap-1.5">
            <label
              htmlFor="message"
              className="text-sm font-semibold text-[#191919]"
            >
              Your question <span className="text-rose-600">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={5}
              value={form.message}
              onChange={update}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-[#191919] focus:border-[#001489] focus:outline-none focus:ring-2 focus:ring-[#001489]/20"
            />
          </div>

          <button
            type="submit"
            disabled={status.state === "sending"}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-base font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60"
            style={{backgroundColor: DHW_BLUE}}
          >
            <Mail size={18} aria-hidden="true" />
            {status.state === "sending" ? "Sending..." : "Send message"}
          </button>

          {status.state === "sent" && (
            <p
              role="status"
              className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#005B4C]"
            >
              <CheckCircle2 size={18} aria-hidden="true" />
              Your message was sent. Our team will reply by email.
            </p>
          )}
          {status.state === "error" && (
            <p
              role="alert"
              className="mt-4 flex items-start gap-2 text-sm font-semibold text-rose-700"
            >
              <AlertCircle size={18} aria-hidden="true" className="shrink-0" />
              <span>
                {status.message} You can also call us at{" "}
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="underline">
                  {SUPPORT_PHONE_DISPLAY}
                </a>
                .
              </span>
            </p>
          )}
        </form>

        {/* Info card */}
        <aside
          className="rounded-[2rem] border p-5 md:p-7"
          style={{borderColor: "#CBD3F0", backgroundColor: "#EEF1FB"}}
        >
          <h3 className="text-lg font-bold text-[#191919]">
            Case Management Services Line
          </h3>
          <a
            href={`tel:${SUPPORT_PHONE_TEL}`}
            className="mt-2 inline-flex items-center gap-2 text-2xl font-bold"
            style={{color: DHW_BLUE}}
          >
            <Phone size={20} aria-hidden="true" />
            {SUPPORT_PHONE_DISPLAY}
          </a>
          <p className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <Clock size={16} aria-hidden="true" />
            Monday&ndash;Friday, 8:00 a.m.&ndash;6:00 p.m. MT
          </p>
          <p className="mt-4 text-sm text-slate-600">
            Call this line if you are new and need help getting started, want to
            schedule an appointment, or are not sure what your next step should
            be.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default ContactPage;
